import { getToken, removeToken } from './auth';
import { withBasePath } from './base-path';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? withBasePath('/api');

/**
 * Error de API con el código estructurado que devuelve el backend
 * (`{ error, code }`). Permite a la UI decidir por código (p. ej. cerrar un
 * modal cuando la acción ya no está pendiente) sin parsear el mensaje.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options?.headers,
  };

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (res.status === 401) {
    const body = await res.json().catch(() => ({}));
    // Only redirect on 401 for protected routes, not auth endpoints
    const isAuthEndpoint = path.startsWith('/auth/');
    if (!isAuthEndpoint) {
      removeToken();
      window.location.href = withBasePath('/login');
    }
    throw new Error(body.error ?? 'Unauthorized');
  }

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(body.error ?? `Request failed: ${res.status}`, res.status, body.code);
  }

  return res.json();
}

export const swrFetcher = <T>(path: string) => apiFetch<T>(path);

/**
 * Descarga un archivo (CSV, XLSX, etc.) desde un endpoint autenticado y
 * dispara la descarga en el browser. Usa el token JWT del mismo localStorage
 * que apiFetch. Lanza Error si el endpoint no devuelve 2xx.
 */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (res.status === 401) {
    removeToken();
    window.location.href = withBasePath('/login');
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Download failed: ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
